import React, { useEffect } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import { useQuery, gql } from '@apollo/client';
import { Spinner } from 'equasion-lib';
import ApiError from '@/lib/shared-coms/ApiError';
import {
  playFailTone,
  playSuccessTone,
  takeExpectedScanHit
} from '@/lib/scanning/feedback';
import ScannedOptions from './ScannedOptions';
import { useRootStore } from '@/lib/providers/RootProvider';

const SERIALIZED_ITEM_STATE = gql`
  query serializedItemState($serialNumber: String!, $gtin: String, $companyId: ID!) {
    serializedItemState(serialNumber: $serialNumber, gtin: $gtin, companyId: $companyId) {
      status
      lot
      expiration
      serialNumber
      gtin
      purchaseOrder {
        id
        clientPurchaseOrderNumber
      }
      salesOrder {
        id
        vendorSalesOrderNumber
      }
    }
  }
`;

const ScannedOptionsData = (props) => {
  const [searchParams] = useSearchParams();
  const orderNumber = searchParams.get('orderNumber');
  const serialNumber = searchParams.get('serialNumber');
  const gtin = searchParams.get('gtin');
  const selectedCompanyId = useRootStore((state) => state.selectedCompany?.id);
  const { data, error, loading } = useQuery(SERIALIZED_ITEM_STATE, {
    fetchPolicy: 'network-only',
    skip: !orderNumber && !serialNumber,
    variables: Object.assign(
      { companyId: selectedCompanyId },
      orderNumber ? { orderNumber } : {},
      serialNumber ? { serialNumber } : {},
      gtin ? { gtin } : {}
    )
  });

  // The home scanner navigates here before it knows whether the serial is a real item.
  // The chime waits for that answer: a loaded item is a hit, a failed lookup is not.
  // Hold the flag until the selected company is in place, so a lookup that starts before
  // that id exists cannot spend the chime on its own failure.
  useEffect(() => {
    if (loading || !selectedCompanyId) return;
    if (!takeExpectedScanHit()) return;
    if (error || !data?.serializedItemState) playFailTone();
    else playSuccessTone();
  }, [loading, error, data, selectedCompanyId]);

  if (loading) return <Spinner />;
  if (error) return <Navigate to="/" />;

  const { serializedItemState } = data || {};

  return <ScannedOptions serializedItemState={serializedItemState} {...props} />;
};

export default ScannedOptionsData;
